import { supportsFill } from "../native/appearance";
import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import type { Camera } from "../native/geometry";
import type { ImageStore } from "../render/image-store";
import type { NativeCrop } from "./native-crop";
import { NativeRegions } from "./native-regions";
import { NativePaths } from "./native-paths";
import { NativeTextEditor } from "./native-text";
import { NativeToolGroups, type WorkTool } from "./native-tool-groups";
import { NativeLayerGroups } from "./native-layer-groups";
export class NativeWorkbench {
  readonly regions: NativeRegions;
  readonly paths: NativePaths;
  readonly text: NativeTextEditor;
  readonly groups: NativeLayerGroups;
  readonly tools: NativeToolGroups;
  tool: WorkTool | null = null;
  private changing = false;
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    overlay: HTMLCanvasElement,
    camera: Camera,
    images: ImageStore,
    private crop: NativeCrop,
    private changed: () => void,
    private blocked: () => boolean,
    message: (text: string, error?: boolean) => void,
  ) {
    const select = () => pointer.setTool("select");
    this.regions = new NativeRegions(
      session,
      pointer,
      overlay,
      camera,
      images,
      changed,
      select,
      blocked,
      message,
    );
    this.paths = new NativePaths(
      session,
      pointer,
      overlay,
      camera,
      changed,
      select,
      blocked,
    );
    this.text = new NativeTextEditor(
      session,
      pointer,
      overlay,
      camera,
      changed,
      select,
      blocked,
      message,
    );
    this.groups = new NativeLayerGroups(
      session,
      changed,
      () => blocked() || this.active || this.busy,
      select,
    );
    this.tools = new NativeToolGroups((tool) => this.activate(tool));
    pointer.onToolChange = () => {
      if (!this.changing) this.activate(null);
    };
    crop.selectionRegion = () => this.regions.region?.box ?? null;
    for (const type of [
      "pointerdown",
      "pointermove",
      "pointerup",
      "pointercancel",
    ] as const)
      overlay.addEventListener(
        type,
        (e) => {
          if (crop.active) return;
          if (this.busy) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return;
          }
          if (
            this.text.event(e) ||
            this.paths.event(e) ||
            this.regions.event(e)
          ) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        },
        true,
      );
    window.addEventListener(
      "keydown",
      (e) => {
        if (blocked()) return;
        if (e.key === "Escape" && this.tools.close()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        if (this.text.key(e) || this.regions.key(e) || this.paths.key(e)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        if (
          !(e.target as HTMLElement).closest(
            "input,textarea,select,[contenteditable=true]",
          ) &&
          e.code === "KeyV" &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.altKey
        ) {
          select();
          e.preventDefault();
        }
        if (
          e.key === "Escape" &&
          this.groups.interacting &&
          !(e.target as HTMLElement).closest("input")
        ) {
          this.groups.cancel();
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      true,
    );
    window.addEventListener("blur", () => {
      this.text.cancelGesture();
      this.regions.cancelGesture();
      this.paths.cancel();
      changed();
    });
  }
  activate(tool: WorkTool | null) {
    if (this.changing || this.blocked()) return;
    this.changing = true;
    this.tools?.close();
    this.text.activate(tool);
    this.paths.activate(tool);
    this.regions.activate(tool);
    this.pointer.cancel();
    if (tool) {
      this.crop.close();
      this.pointer.setTool("select");
      if (!tool.endsWith("-area")) this.session.select([]);
    }
    this.tool = tool;
    this.pointer.external = !!tool;
    this.changing = false;
    this.changed();
  }
  get active() {
    return this.regions.active || this.paths.active || this.text.active;
  }
  get busy() {
    return this.regions.busy;
  }
  finish() {
    this.text.finish();
  }
  render(ctx: CanvasRenderingContext2D) {
    this.groups.render();
    this.regions.render(ctx);
    this.paths.render(ctx);
    this.text.render(ctx);
    this.tools.render(this.tool);
    if (this.tool) {
      document
        .getElementById("native-select")!
        .setAttribute("aria-pressed", "false");
      document.getElementById("native-selection-title")!.textContent =
        this.tool.endsWith("-area")
          ? "Выделение области"
          : this.tool.startsWith("text")
            ? "Текст"
            : "Линия";
    }
    if (this.session.selected?.kind === "text")
      document.getElementById("native-selection-title")!.textContent = "Текст";
    if (this.tool || this.text.editing) {
      const closedPath =
        (this.tool === "pen" || this.tool === "bezier") &&
        !this.paths.active &&
        this.session.selectedNodes.some(
          (n) => n.kind === "path" && supportsFill(n),
        );
      document.getElementById("native-compact-properties")!.hidden =
        !closedPath;
      for (const s of document.querySelectorAll<HTMLElement>(
        "[data-property-section]",
      ))
        s.hidden = !closedPath || s.dataset.propertySection !== "appearance";
    }
  }
}
